import { Expr } from "@redotech/redo-automations-model/expr/ast";
import { evaluate } from "@redotech/redo-automations-model/expr/evaluate";
import { GraphAnalysis } from "@redotech/redo-automations-model/graph/analysis";
import {
  AutomationDefinition,
  DEFAULT_PORT,
  ERROR_PORT,
  StepNode,
  TRIGGER_NODE_ID,
  nextNodeId,
  stepFieldName,
} from "@redotech/redo-automations-model/graph/automation";
import {
  AutomationTypes,
  buildAutomationTypes,
} from "@redotech/redo-automations-model/graph/context-types";
import {
  Diagnostic,
  validateAutomation,
} from "@redotech/redo-automations-model/graph/validate";
import {
  JsonObject,
  JsonValue,
  isJsonObject,
} from "@redotech/redo-automations-model/json";
import {
  ENTITY_GONE,
  mergeRefreshed,
  refreshRequests,
} from "@redotech/redo-automations-model/registry/freshness";
import {
  EffectCall,
  EffectResult,
  EffectTypeDefinition,
  StepKind,
  StepOutcome,
  TriggerKind,
  TriggerRefresh,
} from "@redotech/redo-automations-model/registry/kinds";
import { KindRegistry } from "@redotech/redo-automations-model/registry/registry";
import {
  RESOLVE_EFFECT,
  ResolveAnswer,
  deleteAt,
  planResolution,
  writeAt,
} from "./resolve";
import {
  Advance,
  AdvanceInput,
  NodeRecord,
  PendingEffect,
  RunState,
} from "./state";

export const TRIGGER_REFRESH_EFFECT = "trigger.refresh";
export const TIMER_EFFECT = "automation.timer";
export const AWAIT_EVENT_EFFECT = "automation.event.await";

/** Effects the core itself requests; every registry includes them. */
export const CORE_EFFECTS: EffectTypeDefinition[] = [
  {
    id: RESOLVE_EFFECT,
    class: "read",
    title: "Load data",
    retry: {
      maxAttempts: 3,
      initialIntervalMs: 5000,
      backoff: 1,
      timeoutMs: 30_000,
    },
    idempotent: true,
  },
  {
    id: TRIGGER_REFRESH_EFFECT,
    class: "read",
    title: "Refresh trigger data",
    retry: {
      maxAttempts: 3,
      initialIntervalMs: 5000,
      backoff: 1,
      timeoutMs: 30_000,
    },
    idempotent: true,
  },
  {
    id: TIMER_EFFECT,
    class: "wait",
    title: "Wait",
    retry: { maxAttempts: 1, initialIntervalMs: 0, backoff: 1, timeoutMs: 0 },
    idempotent: true,
  },
  {
    id: AWAIT_EVENT_EFFECT,
    class: "wait",
    title: "Wait for event",
    retry: { maxAttempts: 1, initialIntervalMs: 0, backoff: 1, timeoutMs: 0 },
    idempotent: true,
  },
];

export interface CompiledAutomation {
  automation: AutomationDefinition;
  registry: KindRegistry;
  analysis: GraphAnalysis;
  types: AutomationTypes;
  trigger: TriggerKind;
  /** The trigger's live entities for this automation's trigger config. */
  refresh: TriggerRefresh | null;
}

export type CompileResult =
  | { ok: true; compiled: CompiledAutomation }
  | { ok: false; diagnostics: Diagnostic[] };

/**
 * Compiles an automation being authored: any error refuses it (publishing, tests and
 * simulation all go by this). Every host runs the same compiled automation through
 * the same core.
 */
export function compileAutomation(
  automation: AutomationDefinition,
  registry: KindRegistry,
): CompileResult {
  const result = validateAutomation(automation, registry);
  const errors = result.diagnostics.filter((d) => d.severity === "error");
  const trigger = registry.triggers.get(automation.trigger.kind);
  const types = result.types;
  if (errors.length > 0 || !types || !trigger) {
    return { ok: false, diagnostics: errors };
  }
  return {
    ok: true,
    compiled: compiled(
      automation,
      registry,
      { analysis: result.analysis, types },
      trigger,
    ),
  };
}

/**
 * Compiles an automation that was checked when it was published. Only what stops it
 * running at all refuses it (diagnostics marked blocksExecution), not checks
 * added since, so a published version keeps running under newer rules.
 */
export function compileExecutable(
  automation: AutomationDefinition,
  registry: KindRegistry,
): CompileResult {
  const result = validateAutomation(automation, registry);
  const blocking = result.diagnostics.filter((d) => d.blocksExecution);
  const trigger = registry.triggers.get(automation.trigger.kind);
  if (blocking.length > 0 || !trigger) {
    return { ok: false, diagnostics: blocking };
  }
  // validateAutomation types a trigger config that fails its checks by the default config; a run uses its own.
  const types = trigger.parse(automation.trigger.config).ok
    ? result.types
    : buildAutomationTypes(automation, registry, result.analysis);
  if (!types) {
    return { ok: false, diagnostics: blocking };
  }
  return {
    ok: true,
    compiled: compiled(
      automation,
      registry,
      { analysis: result.analysis, types },
      trigger,
    ),
  };
}

function compiled(
  automation: AutomationDefinition,
  registry: KindRegistry,
  result: { analysis: GraphAnalysis; types: AutomationTypes },
  trigger: TriggerKind,
): CompiledAutomation {
  return {
    automation,
    registry,
    analysis: result.analysis,
    types: result.types,
    trigger,
    refresh: trigger.refreshFor(automation.trigger.config),
  };
}

export function startRun(
  c: CompiledAutomation,
  input: {
    runId: string;
    payload: JsonValue;
    now: string;
    seed?: string;
    forcedRandom?: Record<string, number>;
  },
): Advance {
  const context: JsonObject = {
    trigger: input.payload,
    run: {
      id: input.runId,
      automationId: c.automation.id,
      automationVersion: c.automation.version,
      startedAt: input.now,
    },
    steps: {},
  };
  for (const ext of c.types.extensions) {
    context[ext.field] = ext.value(input.payload, c.trigger);
  }
  const state: RunState = {
    runId: input.runId,
    automationId: c.automation.id,
    automationVersion: c.automation.version,
    status: "running",
    startedAt: input.now,
    now: input.now,
    context,
    phase: { type: "admission", step: "filter" },
    pending: null,
    resolved: [],
    history: [],
    admission: [],
    seq: 0,
    seed: input.seed ?? input.runId,
    triggerStale: false,
    ...(input.forcedRandom ? { forcedRandom: input.forcedRandom } : {}),
  };
  return drive(c, state);
}

export function advance(
  c: CompiledAutomation,
  state: RunState,
  input: AdvanceInput,
): Advance {
  const pending = state.pending;
  if (!pending || pending.id !== input.effectId) {
    throw new Error(
      `Run ${state.runId} is not waiting for effect ${input.effectId}`,
    );
  }
  let next: RunState = { ...state, pending: null, now: input.now };
  const record = {
    id: pending.id,
    type: pending.call.type,
    input: pending.call.input,
    result: input.result,
  };

  if (pending.purpose === "resolve") {
    next = appendEffect(next, pending.nodeId, record);
    const failure = resolutionFailure(pending, input.result);
    if (failure) {
      return pending.nodeId === null
        ? { state: fail(next, null, failure), effect: null }
        : drive(c, failStep(c, next, pending.nodeId, failure));
    }
    next = applyResolution(next, pending, input.result);
    return drive(c, next);
  }

  if (pending.purpose === "refresh") {
    next = appendEffect(next, pending.nodeId, record);
    if (input.result.ok) {
      next = withPayload(
        c,
        next,
        mergeRefreshed(
          c.refresh,
          next.context.trigger ?? null,
          input.result.value,
        ),
      );
    } else if (
      input.result.error.code === ENTITY_GONE &&
      pending.nodeId !== null
    ) {
      // As a lookup of a missing record falls back: the run continues on the last values read.
      const note = `Kept the last trigger data read: ${input.result.error.message}`;
      next = updateRecord(next, pending.nodeId, (r) => ({ ...r, note }));
    }
    const phase = next.phase;
    if (phase && phase.type === "node") {
      next = {
        ...next,
        triggerStale: false,
        phase: { ...phase, step: "execute" },
      };
    }
    return drive(c, next);
  }

  const phase = next.phase;
  if (!phase || phase.type !== "node" || pending.nodeId === null) {
    throw new Error("Step effect without an active step");
  }
  next = appendEffect(next, pending.nodeId, record);
  const effectType = c.registry.effects.get(pending.call.type);
  if (effectType?.class === "wait") {
    next = { ...clearResolved(next), triggerStale: true };
  } else if (effectType?.invalidatesLookups) {
    next = clearResolved(next);
  }
  const node = nodeOf(c, phase.nodeId);
  const kind = kindOf(c, node);
  const outcome = safely(() =>
    kind.onEffectResult(
      runContext(
        c,
        next,
        node,
        phase.nodeState,
        evaluateInputs(c, next, node, kind),
      ),
      input.result,
    ),
  );
  return drive(c, applyOutcome(c, next, node, kind, outcome));
}

function drive(c: CompiledAutomation, initial: RunState): Advance {
  let state = initial;
  for (let guard = 0; guard < 10_000; guard += 1) {
    if (state.status !== "running" || state.pending) {
      return { state, effect: state.pending };
    }
    const phase = state.phase;
    if (!phase) {
      return { state: { ...state, status: "completed" }, effect: null };
    }
    if (phase.type === "admission") {
      state = stepAdmission(c, state, phase.step);
      continue;
    }
    const node = nodeOf(c, phase.nodeId);
    const kind = kindOf(c, node);
    switch (phase.step) {
      case "refresh": {
        state = stepRefresh(c, state, node, kind);

        break;
      }
      case "execute": {
        state = stepExecute(c, state, node, kind);

        break;
      }
      case "awaiting-effect": {
        return { state, effect: state.pending };
      }
    }
  }
  throw new Error(`Run ${state.runId} did not settle`);
}

function stepAdmission(
  c: CompiledAutomation,
  state: RunState,
  step: "filter" | "entry",
): RunState {
  if (step === "filter") {
    const filter = c.trigger.filter(c.automation.trigger.config);
    if (filter) {
      const resolving = requestResolution(
        c,
        state,
        c.types.policyContext,
        [filter],
        null,
      );
      if (resolving) {
        return resolving;
      }
      const matched = safeEvaluate(filter, state);
      if (!matched.ok) {
        return fail(state, null, matched.error);
      }
      if (matched.value !== true) {
        return {
          ...state,
          status: "rejected",
          phase: null,
          rejection: "filtered",
        };
      }
    }
    return { ...state, phase: { type: "admission", step: "entry" } };
  }
  if (step === "entry") {
    const condition = c.automation.policies.entryCondition;
    if (condition) {
      const resolving = requestResolution(
        c,
        state,
        c.types.policyContext,
        [condition],
        null,
      );
      if (resolving) {
        return resolving;
      }
      const admitted = safeEvaluate(condition, state);
      if (!admitted.ok) {
        return fail(state, null, admitted.error);
      }
      if (admitted.value !== true) {
        return {
          ...state,
          status: "rejected",
          phase: null,
          rejection: "entry-conditions",
        };
      }
    }
  }
  return enterNode(
    c,
    state,
    nextNodeId(c.automation, TRIGGER_NODE_ID, DEFAULT_PORT),
  );
}

function stepRefresh(
  c: CompiledAutomation,
  state: RunState,
  node: StepNode,
  kind: StepKind,
): RunState {
  const toExecute: RunState = {
    ...state,
    phase: { type: "node", nodeId: node.id, step: "execute", nodeState: null },
  };
  const refresh = c.refresh;
  if (!state.triggerStale) {
    return toExecute;
  }
  const entities = refreshRequests(refresh, state.context.trigger);
  if (entities.length === 0) {
    return { ...toExecute, triggerStale: false };
  }
  return withEffect(toExecute, node.id, "refresh", {
    type: TRIGGER_REFRESH_EFFECT,
    input: {
      triggerKind: c.trigger.id,
      payload: state.context.trigger ?? null,
      entities: entities.map((e) => ({ ...e })),
    },
  });
}

function withPayload(
  c: CompiledAutomation,
  state: RunState,
  payload: JsonValue,
): RunState {
  const context: JsonObject = { ...state.context, trigger: payload };
  for (const ext of c.types.extensions) {
    const previous = context[ext.field];
    const fresh = ext.value(payload, c.trigger);
    context[ext.field] =
      isJsonObject(previous) && isJsonObject(fresh)
        ? { ...previous, ...fresh }
        : fresh;
  }
  return { ...state, context };
}

function stepExecute(
  c: CompiledAutomation,
  state: RunState,
  node: StepNode,
  kind: StepKind,
): RunState {
  if (kind.skippable && node.disabled) {
    return finishDisabled(c, state, node, kind);
  }
  const bindings = kind.bindings(node.config);
  const resolving = requestResolution(
    c,
    state,
    c.types.contextAt(node.id),
    bindings.map((b) => b.expr),
    node.id,
  );
  if (resolving) {
    return resolving;
  }
  let inputs: Record<string, JsonValue>;
  try {
    inputs = evaluateInputs(c, state, node, kind);
  } catch (e) {
    return fail(state, node.id, e instanceof Error ? e.message : String(e));
  }
  const outcome = safely(() =>
    kind.run(runContext(c, state, node, null, inputs)),
  );
  // A step that calls an effect is recorded by the effect's input, which its
  // inputs would only repeat.
  const recorded =
    outcome.type === "effect"
      ? state
      : updateRecord(state, node.id, (r) => ({ ...r, inputs }));
  return applyOutcome(c, recorded, node, kind, outcome);
}

function applyOutcome(
  c: CompiledAutomation,
  state: RunState,
  node: StepNode,
  kind: StepKind,
  outcome: StepOutcome,
): RunState {
  switch (outcome.type) {
    case "fail":
      return failStep(c, state, node.id, outcome.error);
    case "rewind": {
      const target = guardingCondition(c, state, node.id);
      const next = updateRecord(state, node.id, (r) => ({
        ...r,
        outcome: "completed",
        note: `Re-checking from ${target}`,
        finishedAt: state.now,
      }));
      return enterNode(c, next, target);
    }
    case "effect":
      return withEffect(
        {
          ...state,
          phase: {
            type: "node",
            nodeId: node.id,
            step: "awaiting-effect",
            nodeState: outcome.state ?? null,
          },
        },
        node.id,
        "step",
        outcome.effect,
      );
    case "next": {
      let next = state;
      if (kind.output(node.config)) {
        const steps = isJsonObject(next.context.steps)
          ? next.context.steps
          : {};
        next = {
          ...next,
          context: {
            ...next.context,
            steps: {
              ...steps,
              [stepFieldName(node.id)]: outcome.output ?? null,
            },
          },
        };
      }
      next = updateRecord(next, node.id, (r) => ({
        ...r,
        outcome: "completed",
        port: outcome.port,
        finishedAt: next.now,
        // A declared output is stored once, in the context (see withStepOutputs).
        ...(outcome.output !== undefined && !kind.output(node.config)
          ? { output: outcome.output }
          : {}),
      }));
      return enterNode(
        c,
        next,
        nextNodeId(c.automation, node.id, outcome.port),
      );
    }
  }
}

/** A step failure goes to the step's error port when it's connected; otherwise the run fails. */
function failStep(
  c: CompiledAutomation,
  state: RunState,
  nodeId: string,
  error: string,
): RunState {
  const handler = nextNodeId(c.automation, nodeId, ERROR_PORT);
  if (!handler) {
    return fail(state, nodeId, error);
  }
  const next = updateRecord(state, nodeId, (r) => ({
    ...r,
    outcome: "failed",
    port: ERROR_PORT,
    note: error,
    finishedAt: state.now,
  }));
  return enterNode(c, next, handler);
}

/**
 * The earliest condition in the unbroken run of branching steps the run
 * passed through right before `nodeId`; `nodeId` itself when there is none.
 */
function guardingCondition(
  c: CompiledAutomation,
  state: RunState,
  nodeId: string,
): string {
  let target = nodeId;
  const records = state.history.filter(
    (r) => r.outcome === "completed" || r.nodeId === nodeId,
  );
  const index = records.map((r) => r.nodeId).lastIndexOf(nodeId);
  for (let i = index - 1; i >= 0; i -= 1) {
    const record = records[i];
    if (!record) {
      break;
    }
    const kind = c.registry.steps.get(record.kind);
    const node = c.automation.nodes.find((n) => n.id === record.nodeId);
    if (!kind || !node || kind.category !== "condition") {
      break;
    }
    if (kind.explore(node.config).type === "branch") {
      target = record.nodeId;
    }
  }
  return target;
}

/** Why a run moved onto a newer version was cancelled. */
export const STEP_REMOVED_MID_RUN = "step_removed_mid_run";

/** The step a run is on, and its kind (null when the run has no record of it). */
export interface RunPosition {
  nodeId: string;
  kind: string | null;
}

/** Where the run is in its automation; null outside a step (admitting, or finished). */
export function runPosition(state: RunState): RunPosition | null {
  const phase = state.phase;
  if (!phase || phase.type !== "node") {
    return null;
  }
  const record = state.history.findLast((r) => r.nodeId === phase.nodeId);
  return { nodeId: phase.nodeId, kind: record?.kind ?? null };
}

/**
 * Whether a run pending an effect of `effectType` moves to a newer version
 * once that effect's result comes in: only waits move it (and only
 * `latest` runs). Every host and the publish preview go by this.
 */
export function rebasesAfter(
  registry: KindRegistry,
  effectType: string | null,
): boolean {
  return (
    effectType !== null && registry.effects.get(effectType)?.class === "wait"
  );
}

/**
 * Whether moving a run at `position` onto `next` ends it: its step was
 * removed, or replaced by a different kind of step (whose effect isn't the
 * one the run is waiting on).
 */
export function endsOnRebase(
  next: AutomationDefinition,
  position: RunPosition,
): boolean {
  const node = next.nodes.find((n) => n.id === position.nodeId);
  return !node || (position.kind !== null && position.kind !== node.kind);
}

/**
 * Moves a waiting run onto a newer version of its automation (live edits reach
 * runs that are mid-wait). If endsOnRebase, the run is cancelled instead: it
 * stays on its version and the step it was on is closed out.
 */
export function rebaseRun(
  next: CompiledAutomation,
  state: RunState,
  now: string,
): RunState {
  const position = runPosition(state);
  if (position && endsOnRebase(next.automation, position)) {
    return cancelRun(state, now, STEP_REMOVED_MID_RUN);
  }
  const run = isJsonObject(state.context.run)
    ? { ...state.context.run, automationVersion: next.automation.version }
    : state.context.run;
  return {
    ...state,
    automationVersion: next.automation.version,
    context: { ...state.context, ...(run === undefined ? {} : { run }) },
  };
}

/** A turned-off step: the run leaves by its first port. */
function finishDisabled(
  c: CompiledAutomation,
  state: RunState,
  node: StepNode,
  kind: StepKind,
): RunState {
  const port = kind.ports(node.config)[0]?.id ?? DEFAULT_PORT;
  const next = updateRecord(state, node.id, (r) => ({
    ...r,
    outcome: "disabled",
    port,
    note: "Step is turned off",
    finishedAt: state.now,
  }));
  return enterNode(c, next, nextNodeId(c.automation, node.id, port));
}

function enterNode(
  c: CompiledAutomation,
  state: RunState,
  nodeId: string | undefined,
): RunState {
  if (!nodeId) {
    return { ...state, phase: null, status: "completed" };
  }
  const node = nodeOf(c, nodeId);
  const record: NodeRecord = {
    nodeId,
    kind: node.kind,
    startedAt: state.now,
    effects: [],
  };
  return {
    ...state,
    history: [...state.history, record],
    phase: { type: "node", nodeId, step: "refresh", nodeState: null },
  };
}

function requestResolution(
  c: CompiledAutomation,
  state: RunState,
  root: Parameters<typeof planResolution>[1],
  exprs: Expr[],
  nodeId: string | null,
): RunState | null {
  const planned = planResolution(
    c.types.ts,
    root,
    state.context,
    exprs,
    state.now,
  );
  if (planned.length === 0) {
    return null;
  }
  return withEffect(state, nodeId, "resolve", {
    type: RESOLVE_EFFECT,
    input: {
      requests: planned.map((p) => ({
        location: p.location,
        resolver: p.resolver,
        parent: p.parent,
        args: p.args,
        fallback: p.fallback,
        failRun: p.failRun,
      })),
    },
  });
}

function applyResolution(
  state: RunState,
  pending: PendingEffect,
  result: EffectResult,
): RunState {
  const input = pending.call.input;
  const requests =
    isJsonObject(input) && Array.isArray(input.requests) ? input.requests : [];
  const answers = result.ok && Array.isArray(result.value) ? result.value : [];
  let context = state.context;
  const resolved = [...state.resolved];
  for (const [i, request] of requests.entries()) {
    if (!isJsonObject(request) || !Array.isArray(request.location)) {
      continue;
    }
    const location = request.location.filter(
      (k): k is string | number =>
        typeof k === "string" || typeof k === "number",
    );
    const answer = parseAnswer(answers[i]);
    const value = answer?.ok ? answer.value : (request.fallback ?? null);
    context = writeAt(context, location, value);
    resolved.push(location);
  }
  return { ...state, context, resolved };
}

/** A failed lookup on a field marked onFailure: "fail-run" fails the step. */
function resolutionFailure(
  pending: PendingEffect,
  result: EffectResult,
): string | null {
  const input = pending.call.input;
  const requests =
    isJsonObject(input) && Array.isArray(input.requests) ? input.requests : [];
  if (!result.ok) {
    return requests.some((r) => isJsonObject(r) && r.failRun === true)
      ? result.error.message
      : null;
  }
  const answers = Array.isArray(result.value) ? result.value : [];
  for (const [i, request] of requests.entries()) {
    const answer = parseAnswer(answers[i]);
    if (
      isJsonObject(request) &&
      request.failRun === true &&
      (!answer || !answer.ok)
    ) {
      return `Lookup ${String(request.resolver)} failed${answer && !answer.ok ? `: ${answer.message}` : ""}`;
    }
  }
  return null;
}

function parseAnswer(value: JsonValue | undefined): ResolveAnswer | undefined {
  if (!isJsonObject(value)) {
    return undefined;
  }
  if (value.ok === true) {
    return { ok: true, value: value.value ?? null };
  }
  return {
    ok: false,
    message:
      typeof value.message === "string" ? value.message : "Resolver failed",
  };
}

function clearResolved(state: RunState): RunState {
  let context = state.context;
  for (const location of [...state.resolved].sort(
    (a, b) => b.length - a.length,
  )) {
    context = deleteAt(context, location);
  }
  return { ...state, context, resolved: [] };
}

function withEffect(
  state: RunState,
  nodeId: string | null,
  purpose: PendingEffect["purpose"],
  effect: EffectCall,
): RunState {
  const seq = state.seq + 1;
  return {
    ...state,
    seq,
    pending: {
      id: `${state.runId}:${nodeId ?? "admission"}:${seq}`,
      nodeId,
      purpose,
      call: effect,
    },
  };
}

function appendEffect(
  state: RunState,
  nodeId: string | null,
  record: NodeRecord["effects"][number],
): RunState {
  if (nodeId === null) {
    return { ...state, admission: [...state.admission, record] };
  }
  return updateRecord(state, nodeId, (r) => ({
    ...r,
    effects: [...r.effects, record],
  }));
}

function updateRecord(
  state: RunState,
  nodeId: string,
  fn: (r: NodeRecord) => NodeRecord,
): RunState {
  const history = [...state.history];
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const record = history[i];
    if (record && record.nodeId === nodeId) {
      history[i] = fn(record);
      return { ...state, history };
    }
  }
  return state;
}

function fail(
  state: RunState,
  nodeId: string | null,
  message: string,
): RunState {
  const next = nodeId
    ? updateRecord(state, nodeId, (r) => ({
        ...r,
        outcome: "failed",
        note: message,
        finishedAt: state.now,
      }))
    : state;
  return {
    ...next,
    status: "failed",
    phase: null,
    failure: { nodeId, message },
  };
}

/** The step a run is on, or the one its pending effect is for. */
function activeNodeId(state: RunState): string | null {
  return state.phase?.type === "node"
    ? state.phase.nodeId
    : (state.pending?.nodeId ?? null);
}

/** The active step, when its record is still open (started, not finished). */
function openNodeId(state: RunState): string | null {
  const nodeId = activeNodeId(state);
  return nodeId !== null &&
    state.history.findLast((r) => r.nodeId === nodeId)?.finishedAt === undefined
    ? nodeId
    : null;
}

/**
 * A run its host gave up on (it can't advance): failed at the step it was
 * on, whose record is closed out like a failing step's.
 */
export function abandonRun(
  state: RunState,
  message: string,
  now: string,
): RunState {
  const nodeId = activeNodeId(state);
  return {
    ...fail({ ...state, now }, openNodeId(state), message),
    pending: null,
    failure: { nodeId, message },
  };
}

/** Why a run whose host stopped it was cancelled. */
export const RUN_CANCELLED = "run_cancelled";

/** A run its host stopped before it finished: cancelled, closing out the step it was on. */
export function cancelRun(
  state: RunState,
  now: string,
  reason: string = RUN_CANCELLED,
): RunState {
  const nodeId = openNodeId(state);
  const next =
    nodeId !== null
      ? updateRecord(state, nodeId, (r) => ({
          ...r,
          outcome: "cancelled",
          finishedAt: now,
        }))
      : state;
  return {
    ...next,
    now,
    status: "cancelled",
    phase: null,
    pending: null,
    rejection: reason,
  };
}

function evaluateInputs(
  c: CompiledAutomation,
  state: RunState,
  node: StepNode,
  kind: StepKind,
): Record<string, JsonValue> {
  const inputs: Record<string, JsonValue> = {};
  for (const binding of kind.bindings(node.config)) {
    inputs[binding.key] = evaluate(binding.expr, {
      data: state.context,
      now: state.now,
    });
  }
  return inputs;
}

function safeEvaluate(
  expr: Expr,
  state: RunState,
): { ok: true; value: JsonValue } | { ok: false; error: string } {
  try {
    return {
      ok: true,
      value: evaluate(expr, { data: state.context, now: state.now }),
    };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

function safely(fn: () => StepOutcome): StepOutcome {
  try {
    return fn();
  } catch (e) {
    return { type: "fail", error: e instanceof Error ? e.message : String(e) };
  }
}

function runContext(
  c: CompiledAutomation,
  state: RunState,
  node: StepNode,
  nodeState: JsonValue,
  inputs: Record<string, JsonValue>,
) {
  return {
    config: node.config,
    inputs,
    nodeId: node.id,
    run: {
      runId: state.runId,
      automationId: state.automationId,
      automationVersion: state.automationVersion,
      now: state.now,
      startedAt: state.startedAt,
    },
    state: nodeState,
    random:
      state.forcedRandom?.[node.id] ??
      deterministicRandom(`${state.seed}:${node.id}`),
  };
}

function nodeOf(c: CompiledAutomation, nodeId: string): StepNode {
  const node = c.automation.nodes.find((n) => n.id === nodeId);
  if (!node) {
    throw new Error(`Unknown node ${nodeId}`);
  }
  return node;
}

function kindOf(c: CompiledAutomation, node: StepNode): StepKind {
  const kind = c.registry.steps.get(node.kind);
  if (!kind) {
    throw new Error(`Unknown step kind ${node.kind}`);
  }
  return kind;
}

/** FNV-1a over the seed: the same run and node always draw the same number. */
export function deterministicRandom(seed: string): number {
  let hash = 0x81_1c_9d_c5;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01_00_01_93);
  }
  return (hash >>> 0) / 0x1_00_00_00_00;
}
